import { config } from '@/lib/config';
import type { ApiErrorBody, AuthTokens, Ok } from './types';
import { clearSession, getRefreshToken, setTokens } from '@/lib/auth/session';
import { AppError } from './errors';

/**
 * Single-flight refresh (plan §4.2).
 *
 * Concurrent 401s share ONE in-flight refresh via a memoized promise; never two
 * concurrent refreshes. On success the new token pair is stored and the promise resolves;
 * on failure (cookie expired, or reuse-detection family invalidation per refresh.md) the
 * session is cleared and onRefreshFailure() fires so the app can hard-logout.
 */
let inFlight: Promise<void> | null = null;
let onFailure: (() => void) | null = null;

export function setRefreshFailureHandler(fn: () => void): void {
  onFailure = fn;
}

async function doRefresh(): Promise<void> {
  // Low-level fetch (NOT the intercepted client) to avoid recursion. credentials:'include'
  // sends the httpOnly refresh cookie; body carries the token if the backend uses that mode.
  const refreshToken = getRefreshToken();
  const res = await fetch(`${config.apiBaseUrl}/admin/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
  });

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      /* ignore */
    }
    clearSession();
    onFailure?.();
    throw body
      ? AppError.fromEnvelope(body, res.status)
      : new AppError({ code: 'unauthorized', message: 'Session expired.', status: res.status });
  }

  const json = (await res.json()) as Ok<AuthTokens>;
  setTokens(json.data);
}

/** Trigger (or join) the single in-flight refresh. */
export function refreshSession(): Promise<void> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
